import React, { useCallback, useRef } from "react";
import { Caption, Title } from "./Texts";
import { Button, Grid, TextField, Typography } from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../store";
import { removeFile, setPreferencesPartial } from "../../store/expertise/expertise.reducer";
import { DocumentService } from "../../../core/services/document.service";
import { FichierPrerequisSollicitation, FichierPrerequisSollicitationWithContent } from "../../../core/apis/monsisra/generated";
import DocumentPrerequis from "./documentPrerequis/DocumentPrerequis";
import { cloneDeep } from "lodash";

export default function DocumentsExiges() {
	const { preferences } = useAppSelector((s) => s.expertise);

	const dispatch = useAppDispatch();

	const inputFileRef = useRef<HTMLInputElement>(null);

	const onFileDeleted = useCallback(
		(file: FichierPrerequisSollicitation) => () => {
			 	if (preferences) {
					dispatch(removeFile(file.fileName));
				}
			},
		[preferences, dispatch]
	);

	const onFileSelected = useCallback(
		async (e: React.ChangeEvent<HTMLInputElement>) => {
			const files = e.target.files;

			if (files && preferences) {
				const filesPromises = [...files].map(async (file: File): Promise<FichierPrerequisSollicitationWithContent> => {
					const documentService = new DocumentService();

					return {
						fileName: file.name,
						mimeType: file.type,
						content: await documentService.toBase64(file),
						libelle: file.name,
					};
				});

				dispatch(
					setPreferencesPartial({
						prerequis: {
							...preferences.prerequis,
							files: [...preferences.prerequis!.files!, ...(await Promise.all(filesPromises))],
						},
					})
				);
			}
			// Clean de l'input afin de pouvoir choisir le même fichier
			e.target.value = "";
		},
		[dispatch, preferences]
	);

	const onFileLibelleModified = useCallback(
		(idFichier?: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
			const newLibelle = e.target.value;
			if (newLibelle && preferences) {
				const copy = cloneDeep<FichierPrerequisSollicitation[]>(preferences.prerequis!.files);
				const indexFile = copy.findIndex(file => file.idFichier === idFichier);
				copy[indexFile].libelle = newLibelle;

				dispatch(
					setPreferencesPartial({
						prerequis: {
							...preferences.prerequis,
							files: copy,
						},
					})
				);
			}
		},
		[dispatch, preferences]
	);

	const setDescription = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			let message = e.target.value;
			if (preferences) {
				dispatch(
					setPreferencesPartial({
						prerequis: {
							...preferences.prerequis,
							message: message,
						},
					})
				);
			}
		},
		[dispatch, preferences]
	);

	if (!preferences) return null;

	return (
		<Grid component={"section"} container direction={"column"} spacing={2}>
			<Grid item>
				<Title text={"Documents et prérequis exigés"} />
			</Grid>
			<Grid item>
				<Caption
					text={
						"Décrire ici les prérequis éventuels exigés de vos demandeurs : documents à joindre, éléments, indispensables autour du patient, etc."
					}
				/>
			</Grid>
			<Grid item>
				<TextField
					className={"input"}
					fullWidth
					placeholder={"Champ limité à 800 caractères"}
					multiline
					onChange={setDescription}
					value={preferences.prerequis!.message}
					rows={3}
					inputProps={{
						maxLength: "800",
					}}
				/>
			</Grid>
			<Grid item container direction={"column"} spacing={1}>
				<Grid item>
					<input
						style={{ display: "none" }}
						id="input-document-exige"
						multiple
						type="file"
						onChange={onFileSelected}
						ref={inputFileRef}
					/>
					<label htmlFor="input-document-exige">
						<Button variant="outlined" component="span">
							Choisir un fichier
						</Button>
					</label>
				</Grid>

				<Grid item>
					{preferences.prerequis!.files!.length === 0 && <Typography>Aucun document chargé actuellement</Typography>}
				</Grid>
				<Grid item container spacing={2}>
					{preferences.prerequis!.files!.map((file) => (
						<Grid item key={file.fileName}>
							<DocumentPrerequis
								idDocument={file.idFichier}
								libelle={file.libelle ?? file.fileName}
								downloadUrl={file.downloadUrl}
								onFileDeleted={onFileDeleted(file)}
								onLibelleModified={onFileLibelleModified(file.idFichier)}
							/>
						</Grid>
					))}
				</Grid>
			</Grid>
		</Grid>
	);
}
